import { appTheme } from '../features/Themes/light';
import { Answer } from '../interfaces/answer.interface';
import { PIDetails, SubRemediation } from '../interfaces/assessment.interface';
import { BestPractice } from '../interfaces/bestPractice.interface';
import { Topic } from '../interfaces/topic.interface';
import { FULLY_IMPLEMENTED, NOT_APPLICABLE, NOT_IMPLEMENTED, REMEDIATION_OPTIONS, REMEDIATION_OPTIONS_LABELS, REMEDIATION_VERIFICATION_STATUS, REMEDIATION_VERIFICATION_STATUS_LABELS, WILL_NOT_REMEDIATE_REASON_LABELS } from '../services/surveyHelpers';
import moment from 'moment';
import { USER_TYPES } from '../services/user';
import { GOLD_BADGE_LABEL, SILVER_BADGE_LABEL, GOLD_STAR_BADGE_LABEL, GOLD_NEXT_GEN_BADGE_LABEL } from '../services/badge';
import { Badge } from '../interfaces/users.interface';

export const AssessmentBadgeRank = {
  [GOLD_STAR_BADGE_LABEL]: 4,
  [GOLD_NEXT_GEN_BADGE_LABEL]: 3,
  [SILVER_BADGE_LABEL]: 2,
  [GOLD_BADGE_LABEL]: 1,
};

type BadgeTitle = keyof typeof AssessmentBadgeRank;

export const getHighestRankAssessmentBadgeLabel = (badges: Badge[]): BadgeTitle => {
  let rank = 1; // default GOLD (Classic)
  return badges.reduce<BadgeTitle>((acc, b) => {
    const title = b.title as BadgeTitle;
    if (AssessmentBadgeRank[title] && AssessmentBadgeRank[title] > rank) {
      rank = AssessmentBadgeRank[title];
      acc = title;
    }
    return acc;
  }, GOLD_BADGE_LABEL);
};
  

export const getLegendData = () => {
  return {
    legendHeader: {
      style: 'headerBG',
      margin: [0, 20, 0, 10],
      table: {
        widths: ['*'],
        body: [
          [{
            border: [false, false, false, false],
            fillColor: appTheme.palette.primary.main,
            text: 'Legend',
            color: 'white',
          }],
        ],
      },
    },
    legendBody: {
      margin: [0, 5, 0, 30],
      table: {
        widths: [100, '*'],
        body: [
          [
            {
              image: 'WorkspacePremium',
              width: 14,
              height: 14,
              alignment: 'center',
            },
            { text: 'This question is tied to an MPA Content Security Best Practice' },
          ],
          [
            {
              fillColor: appTheme.palette.success.main,
              color: 'white',
              text: 'Yes',
              alignment: 'center',
            },
            { text: 'The control item is Fully Implemented' },
          ],
          [
            {
              fillColor: appTheme.palette.nonCompliant.main,
              text: 'C/C',
              alignment: 'center',
            },
            { text: 'The control item is addressed with a Compensating Control' },
          ],
          [
            {
              fillColor: appTheme.palette.error.main,
              color: 'white',
              text: 'N/I',
              alignment: 'center',
            },
            { text: 'The control item is Not Implemented' },
          ],
          [
            {
              fillColor: '#ccc',
              text: 'N/A',
              alignment: 'center',
            },
            { text: 'The control item is Not Applicable' },
          ],
          [
            {
              fillColor: appTheme.palette.success.main,
              color: 'white',
              text: 'F/I',
              alignment: 'center',
            },
            { text: 'Fully Implemented' },
          ],
          [
            {
              fillColor: appTheme.palette.nonCompliant.main,
              text: 'P/I',
              alignment: 'center',
            },
            { text: 'Partially Implemented' },
          ],
          [
            {
              fillColor: appTheme.palette.error.main,
              color: 'white',
              text: 'N/I',
              alignment: 'center',
            },
            { text: 'Not Implemented' },
          ],
          [
            {
              fillColor: '#ccc',
              text: 'N/A',
              alignment: 'center',
            },
            { text: 'Not Applicable' },
          ],
        ],
      },
    },
  };
};


export const getFileName = (url: string): string => {
  try {
    return new URL(url).pathname.split('/').pop()!;
  } catch {
    return url.split('?')[0].split('/').pop()!;
  }
};

export const outputBPItems = (bpDisplay: any, userType: string) => {
  const bpItems:any = bpDisplay.map((bpitem: any, index: number) => {
    const item:any = [
      {
        style: 'headerBG',
        margin: [0, 10, 0, 0],
        table: {
          widths: '*',
          body: [
            [{
              border: [false, false, false, false],
              fillColor: appTheme.palette.primary.main,
              text: `${bpitem.domain.code}: ${bpitem.domain.title} - ${bpitem.topic.title}`,
            } ],
            [{
              border: [false, false, false, false],
              fillColor: appTheme.palette.primary.main,
              text: `${bpitem.domain.topics.findIndex((top: Topic) => bpitem.topic.id === top.id) + 1}.${bpitem.topic.bestPractices.findIndex((bp: BestPractice) => bpitem.bp.id === bp.id)} ${bpitem.bp.title}`,
            } ],
          ],
        },
      },
    ];
    bpitem.questions.map((qContain: any) => {
      const fullyImplementedAnswers = qContain.question.answers.filter((answer: Answer) => ![NOT_APPLICABLE, NOT_IMPLEMENTED].includes(answer.text || ''));
      const isFullyImplemented = qContain.answer?.answer.length === fullyImplementedAnswers.length;
      const hasNoAnswer = !qContain.answer?.answer?.length
          && !qContain.answer?.compensatingControlAnswers?.length
          && !qContain.answer?.nonApplicableAnswers?.length
          &&  !qContain.answer?.compensatingControlAnswers?.length;

      const assessorAnswer = qContain.assessorAnswer.answer;
      const subRemediationCount = qContain.assessorAnswer.remediationQuestionAnswers.length;
      const subRemediationWithPlanCount = qContain.assessorAnswer.remediationQuestionAnswers.filter((rqa: SubRemediation) => !!rqa.remediationPlan).length;

      const notImplementedAnswer = qContain.question.answers.find((a: Answer) => a.text === NOT_IMPLEMENTED);
      const notApplicableAnswer = qContain.question.answers.find((a: Answer) => a.text === NOT_APPLICABLE);
      const answerIsNotImplemented = qContain.answer?.answer.includes(notImplementedAnswer?.id);
      const assessorAnswerFullyImplemented = qContain.assessorAnswer.answer === FULLY_IMPLEMENTED;
      const widthsArray = !assessorAnswerFullyImplemented
        ? ['*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto']
        : ['*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'];

      const attachmentText = qContain.answer?.evidenceAttachments?.map((attachment: any) => {
        const fileName =  attachment?.file?.document ? getFileName(attachment.file.document) : '';
        return `${fileName} (${attachment.isPublic ? 'Public' : 'Private'})`;
      }).join(', ');

      const questionArray:any = [
        qContain.question.isBestPractice
          ? {
            columns: [
              {
                table: {
                  widths: [16],
                  body: [
                    [
                      {
                        image: 'WorkspacePremium',
                        width: 16,
                        margin: [0, 0, 5, 0],
                        valign: 'top',
                      },
                    ],
                  ],
                },
                layout: 'noBorders',
                width: 21,
              },
              {
                fontSize: 16,
                text: qContain.question.title,
                margin: [0, 0, 0, 10],
                bold: true,
              },
            ],
          }
          : {
            fontSize: 16,
            text: qContain.question.title,
            margin: [0, 0, 0, 10],
            bold: true,
          },
        {
          table: {
            widths: widthsArray,
            body: [
              [
                {
                  text: '',
                  border: [true, true, true, false], // (left, top, right, bottom)
                },
                {
                  text: 'Service Provider',
                  colSpan: 4,
                  alignment: 'center',
                  bold: true,
                  fillColor: '#E6F7FF', // soft light blue
                },
                {}, {}, {},
                {
                  text: 'Assessor',
                  colSpan: 4,
                  alignment: 'center',
                  bold: true,
                  fillColor: '#FFFDD0', // gentle light gray
                },
                {}, {}, {},
                ...(!assessorAnswerFullyImplemented ? [
                  {
                    text: 'Remediation Plans',
                    colSpan: 4,
                    alignment: 'center',
                    bold: true,
                    fillColor: '#F3E5F5',
                  },
                  {}, {}, {},
                ] : []),
              ],                
              [
                {
                  text: '',
                  border: [true, false, true, false], 
                },
                {
                  text: isFullyImplemented
                    ? FULLY_IMPLEMENTED
                    : answerIsNotImplemented
                      ? NOT_IMPLEMENTED
                      : qContain.answer?.answer.includes(notApplicableAnswer?.id)
                        ? NOT_APPLICABLE
                        : (hasNoAnswer || qContain?.question?.type === 'single_select')
                          ? ''
                          : 'Partially Implemented',
                  bold: true,
                  colSpan: 4,
                  alignment: 'center',
                  fillColor: isFullyImplemented
                    ? appTheme.palette.success.main
                    : answerIsNotImplemented
                      ? appTheme.palette.error.main
                      : qContain?.answer?.answer.includes(notApplicableAnswer?.id)
                        ? '#ccc'
                        : appTheme.palette.nonCompliant.main,
                  color: isFullyImplemented || answerIsNotImplemented || hasNoAnswer ? 'white' : 'black',
                  margin: [10, 5, 10, 5],
                },
                {}, {}, {},
                {
                  text: assessorAnswer,
                  bold: true,
                  colSpan: 4,
                  alignment: 'center',
                  fillColor: assessorAnswer === FULLY_IMPLEMENTED
                    ? appTheme.palette.success.main
                    : assessorAnswer === NOT_IMPLEMENTED
                      ? appTheme.palette.error.main
                      : assessorAnswer === NOT_APPLICABLE
                        ? '#ccc'
                        : appTheme.palette.nonCompliant.main,
                  color: assessorAnswer === FULLY_IMPLEMENTED || assessorAnswer === NOT_IMPLEMENTED ? 'white' : 'black',
                  margin: [10, 5, 10, 5],
                },
                {},
                {},
                {},
                ...(!assessorAnswerFullyImplemented ? [
                  {
                    text: `${subRemediationWithPlanCount} out of ${subRemediationCount} complete`,
                    colSpan: 4,
                    bold: true,
                    alignment: 'center',
                    fillColor: '#F3E5F5',
                    margin: [10, 5, 10, 5],
                  },
                  {}, {}, {},
                ] : []),
              ],
              [
                {
                  text: '',
                  border: [true, false, false, false], 
                },
                { text: 'YES', alignment: 'center', noWrap: true, fillColor: '#E6F7FF' },
                { text: 'C/C', alignment: 'center', noWrap: true, fillColor: '#E6F7FF' },
                { text: 'N/I', alignment: 'center', noWrap: true, fillColor: '#E6F7FF' },
                { text: 'N/A', alignment: 'center', noWrap: true, fillColor: '#E6F7FF' },
                { text: 'YES', alignment: 'center', noWrap: true, fillColor: '#FFFDD0' },
                { text: 'C/C', alignment: 'center', noWrap: true, fillColor: '#FFFDD0' },
                { text: 'N/I', alignment: 'center', noWrap: true, fillColor: '#FFFDD0' },
                { text: 'N/A', alignment: 'center', noWrap: true, fillColor: '#FFFDD0' },
                ...(!assessorAnswerFullyImplemented ? [
                  { text: 'Remediated', noWrap: true, fillColor: '#F3E5F5' },
                  { text: 'Later', noWrap: true, fillColor: '#F3E5F5' },
                  { text: 'No', noWrap: true, fillColor: '#F3E5F5' },
                  { text: 'Comment', noWrap: true, fillColor: '#F3E5F5' },
                ] : []),
              ],
              ...qContain.question.answers
                .filter((a: Answer) => ![qContain?.question?.type === 'single_select' ? '' : NOT_APPLICABLE, NOT_IMPLEMENTED, FULLY_IMPLEMENTED].includes(a.text || ''))
                .map((answer: Answer) => {
                  const answered = qContain.answer && qContain.answer.answer.findIndex((ans: number) => ans === answer.id) > -1;
                  const answeredCC = qContain.answer && qContain.answer.compensatingControlAnswers.findIndex((ans: number) => ans === answer.id) > -1;
                  const answeredNI = qContain.answer && qContain.answer.nonImplementedAnswers.findIndex((ans: number) => ans === answer.id) > -1;
                  const answeredNA = qContain.answer && qContain.answer.nonApplicableAnswers.findIndex((ans: number) => ans === answer.id) > -1;

                  const assessorAnswered = qContain.assessorAnswer && qContain.assessorAnswer.assessorQuestionAnswers.findIndex((ans: PIDetails) => (typeof ans.answer === 'number'
                    ? ans.answer === answer.id
                    : (ans.answer as Answer)?.id === answer.id) && ans.compensatingOrNotApplicable === 'implemented') > -1;

                  const assessorAnsweredCC = qContain.assessorAnswer && qContain.assessorAnswer.assessorQuestionAnswers.findIndex((ans: PIDetails) => (typeof ans.answer === 'number'
                    ? ans.answer === answer.id
                    : (ans.answer as Answer)?.id === answer.id) && ans.compensatingOrNotApplicable === 'compensating_control') > -1;

                  const assessorAnsweredNI = qContain.assessorAnswer && qContain.assessorAnswer.assessorQuestionAnswers.findIndex((ans: PIDetails) => (typeof ans.answer === 'number'
                    ? ans.answer === answer.id
                    : (ans.answer as Answer)?.id === answer.id) && ans.compensatingOrNotApplicable === 'not_implemented') > -1;

                  const assessorAnsweredNA = qContain.assessorAnswer && qContain.assessorAnswer.assessorQuestionAnswers.findIndex((ans: PIDetails) => (typeof ans.answer === 'number'
                    ? ans.answer === answer.id
                    : (ans.answer as Answer)?.id === answer.id) && ans.compensatingOrNotApplicable === 'not_applicable') > -1;
                  
                  const onlyYesAnswer =
                      answer.text === FULLY_IMPLEMENTED ||
                      answer.text === NOT_IMPLEMENTED ||
                      answer.text === NOT_APPLICABLE;

                  const answerQuestionAnswer = qContain.assessorAnswer && qContain.assessorAnswer.remediationQuestionAnswers.find((ans: PIDetails) => typeof ans.answer === 'number' ? ans.answer === answer.id : (ans.answer as Answer)?.id === answer.id);
                  const answerRemediated = answerQuestionAnswer?.remediationPlan === REMEDIATION_OPTIONS.remediated;
                  const answerWillRemediateLater = answerQuestionAnswer?.remediationPlan === REMEDIATION_OPTIONS.willRemediateLater;
                  const answerWillNotRemediate = answerQuestionAnswer?.remediationPlan === REMEDIATION_OPTIONS.willNotRemediate;
                  const answerRemediationComment = answerQuestionAnswer?.remediationComment;
                  const willNotReason = answerQuestionAnswer?.willNotRemediateReason === 'compensating_control' ? '(Compensating Control): ' : '(Other): ';
                  const answerRemediationEvidence = answerQuestionAnswer?.attachments;
                  const displayRemediationDate = `\nRemediation Date: ${moment(answerQuestionAnswer?.remediationDate).format('MM/DD/YYYY')}`;
                  const formattedValidationText = `\nRemediation Status: ${REMEDIATION_VERIFICATION_STATUS_LABELS[answerQuestionAnswer?.verificationStatus as keyof typeof REMEDIATION_VERIFICATION_STATUS_LABELS]}`;
                  const displayValidationText = userType === USER_TYPES.TPN_ADMIN
                    ? formattedValidationText
                    : userType === USER_TYPES.CONTENT_OWNER && answerQuestionAnswer?.verificationStatus !== REMEDIATION_VERIFICATION_STATUS.notStarted
                      ? formattedValidationText
                      : userType === USER_TYPES.VENDOR_ADMIN && answerQuestionAnswer?.verificationStatus === REMEDIATION_VERIFICATION_STATUS.rejected
                        ? formattedValidationText
                        : '';
                  return [
                    { text: answer.text },
                    { text: answered ? 'X' : '', alignment: 'center', fillColor: '#E6F7FF' },
                    {
                      text: onlyYesAnswer ? '-' : (answeredCC ? 'X' : ''),
                      alignment: 'center',
                      color: onlyYesAnswer ? 'grey' : 'black',
                      fillColor: onlyYesAnswer ? '#d3d3d3' : '#E6F7FF',
                    },
                    {
                      text: onlyYesAnswer ? '-' : (answeredNI ? 'X' : ''),
                      alignment: 'center',
                      color: onlyYesAnswer ? 'grey' : 'black',
                      fillColor: onlyYesAnswer ? '#d3d3d3' : '#E6F7FF',
                    },
                    {
                      text: onlyYesAnswer ? '-' : (answeredNA ? 'X' : ''),
                      alignment: 'center',
                      color: onlyYesAnswer ? 'grey' : 'black',
                      fillColor: onlyYesAnswer ? '#d3d3d3' : '#E6F7FF',
                    },
                    {
                      text: assessorAnswered ? 'X' : '',
                      alignment: 'center',
                      color: 'black',
                      fillColor: '#FFFDD0',
                    },
                    {
                      text: onlyYesAnswer ? '-' : (assessorAnsweredCC ? 'X' : ''),
                      alignment: 'center',
                      color: onlyYesAnswer ? 'grey' : 'black',
                      fillColor: onlyYesAnswer ? '#d3d3d3' : '#FFFDD0',
                    },
                    {
                      text: onlyYesAnswer ? '-' : (assessorAnsweredNI ? 'X' : ''),
                      alignment: 'center',
                      color: onlyYesAnswer ? 'grey' : 'black',
                      fillColor: onlyYesAnswer ? '#d3d3d3' : '#FFFDD0',
                    },
                    {
                      text: onlyYesAnswer ? '-' : (assessorAnsweredNA ? 'X' : ''),
                      alignment: 'center',
                      color: onlyYesAnswer ? 'grey' : 'black',
                      fillColor: onlyYesAnswer ? '#d3d3d3' : '#FFFDD0',
                    },
                    ...(!assessorAnswerFullyImplemented ? [
                      { text: answerRemediated ? 'X' : '', alignment: 'center', fillColor: '#F3E5F5' },
                      { text: answerWillRemediateLater ? 'X' : '', alignment: 'center', fillColor: '#F3E5F5' },
                      { text: answerWillNotRemediate ? 'X' : '', alignment: 'center', fillColor: '#F3E5F5' },
                      {
                        text: answerWillNotRemediate
                          ? willNotReason + answerRemediationComment
                          : answerWillRemediateLater
                            ? answerRemediationComment + displayRemediationDate
                            : answerRemediated
                              ? (answerRemediationComment || '') +
                                  displayRemediationDate +
                                  displayValidationText +
                                  (answerRemediationEvidence?.length
                                    ? `\n(${answerRemediationEvidence?.length} attachment${answerRemediationEvidence?.length > 1 ? 's' : ''})`
                                    : '')
                              : '',
                        fillColor: '#F3E5F5',
                        fontSize: 11,
                      },                          
                    ] : []),                    
                  ];
                }),
              [
                {
                  margin: [0, 5, 0, 0],
                  text: (qContain.answer?.attachments?.length > 0)
                    ? {
                      fontSize: 12,
                      lineHeight: 1.2,
                      text: [
                        { text: 'Attachments: ', bold: true },
                        { text: attachmentText },
                      ],
                      margin: [0, 0, 0, 0],
                      border: [false, false, false, false],
                    }
                    : '',
                },
                {
                  fontSize: 12,
                  lineHeight: 1.2,
                  text: qContain.answer?.comment
                    ? [
                      { text: 'Additional Details: ', bold: true },
                      { text: qContain.answer.comment },
                    ]
                    : '',
                  colSpan: 4,
                  margin: [0, 5, 0, 0],
                },
                {
                  text: '',
                  border: [false, false, false, false],
                },
                {
                  text: '',
                  border: [false, false, false, false],
                },
                {
                  text: '',
                  border: [false, false, false, false],
                },
                {
                  fontSize: 12,
                  lineHeight: 1.2,
                  text: qContain.assessorAnswer?.assessorFinding
                    ? [
                      { text: 'Assessor Findings: ', bold: true },
                      { text: qContain.assessorAnswer.assessorFinding },
                    ]
                    : '',
                  colSpan: 4,
                  margin: [0, 5, 0, 0],
                },
                {},
                {},
                {},
                ...(!assessorAnswerFullyImplemented ? [
                  {
                    fontSize: 12,
                    lineHeight: 1.2,
                    text: '',
                    colSpan: 4,
                    margin: [0, 5, 0, 0],
                  },
                  {},
                  {},
                  {},
                ] : []),
              ],
            ],
          },
          margin: [0, 0, 10, 10],
        },
      ];

      item.push({
        table: {
          headerRows: 0,
          widths: ['*'],
          body: [
            [{
              stack: questionArray,
            }],
          ],
        },
        layout: 'noBorders', 
        margin: [0, 10, 0, 0],
      });
    });
    if (index + 1 !== bpDisplay.length) return { stack: item, pageBreak: 'before' };
    return { stack: item };
  });

  return bpItems;
};

export const legacyOutputBPItems = (bpDisplay: any) => {
  const bpItems:any = bpDisplay.map((bpitem: any, index: number) => {
    const item:any = [
      {
        style: 'headerBG',
        table: {
          widths: '*',
          body: [
            [{
              border: [false, false, false, false],
              fillColor: appTheme.palette.primary.main,
              text: `${bpitem.domain.code}: ${bpitem.domain.title} - ${bpitem.topic.title}`,
            } ],
            [{
              border: [false, false, false, false],
              fillColor: appTheme.palette.primary.main,
              text: `${bpitem.domain.topics.findIndex((top: Topic) => bpitem.topic.id === top.id) + 1}.${bpitem.topic.bestPractices.findIndex((bp: BestPractice) => bpitem.bp.id === bp.id)} ${bpitem.bp.title}`,
            } ],
          ],
        },
      },
    ];
    bpitem.questions.map((qContain: any) => {
      const questionArray:any = [
        qContain.question.isBestPractice
          ? {
            columns: [
              {
                table: {
                  widths: [16],
                  body: [
                    [
                      {
                        image: 'WorkspacePremium',
                        width: 16,
                        margin: [0, 10, 5, 0],
                        valign: 'top',
                      },
                    ],
                  ],
                },
                layout: 'noBorders',
                width: 21,
              },
              {
                fontSize: 16,
                text: qContain.question.title,
                margin: [0, 10, 0, 10],
                bold: true,
              },
            ],
          }
          : {
            fontSize: 16,
            text: qContain.question.title,
            margin: [0, 0, 0, 10],
            bold: true,
          },
      ];
      if (qContain && qContain.answer && qContain.answer.text) { questionArray.push(qContain.answer.text); } else {
        const answers = qContain.question.answers.map((answer: Answer) => {
          const answered = qContain.answer && qContain.answer.answer.findIndex((ans: number) => ans === answer.id) > -1;
          return {
            columns: [
              { margin: [5, 0, 0, 0], width: 'auto', text: answered ? '√' : '-' },
              { width: 'auto', text: answer.text },
            ],
            bold: answered,
            columnGap: 5,
          };
        });
        questionArray.push(...answers);
      }

      const partiallyImplementedSection:  Array<{ text: string; bold?: boolean; margin?: number[] }> = [];
      if (qContain?.assessorAnswer?.answer === 'Partially Implemented' && qContain?.assessorAnswer?.assessorQuestionAnswers?.length) {
        const pIAssessorFindings = [{ text: 'Partially Implemented Details', bold: true, margin: [0, 10, 0, 0] }];
        const pIAssessorFindingsBody = qContain?.question?.answers?.filter((it: Answer) => (qContain.question.type === 'single_select' && qContain.assessorAnswer.assessorQuestionAnswers.some((ans: PIDetails) => it.id === ans.answer) ) || (qContain.question.type !== 'single_select' && it.text !== 'Fully Implemented' && it.text !== NOT_IMPLEMENTED && it.text !== 'Not Applicable')).map((ans: Answer) => {
          const details = qContain.assessorAnswer.assessorQuestionAnswers.find((pIDetails: PIDetails) => pIDetails.answer === ans.id);

          const formatStatus = (status: string) => {
            switch (status) {
              case 'not_applicable':
                return 'Not applicable';
              case 'not_implemented':
                return 'Not implemented';
              case 'compensating_control':
                return 'Compensating control';
              case 'implemented':
                return 'Implemented';
              default:
                return '';
            }
          };

          return [
            {
              columns: [
                { text: `• ${ans.text}`, bold: false },
              ],
              bold: true,
              columnGap: 0,
              margin: [20, 5, 0, 0],
            },
            (details && details.compensatingOrNotApplicable) ? {
              text: [
                { text: 'Status: ', bold: true },
                { text: formatStatus(details.compensatingOrNotApplicable) },
              ],
              margin: [40, 0, 0, 0],
            } : {},
            (details && details.comment) ? {
              text: [
                { text: 'Comment: ', bold: true },
                { text: details.comment },
              ],
              margin: [40, 0, 0, 0],
            } : {},
            {
              text: '',
              margin: [0, 1, 0, 0],
            },
          ];
        }).flat();

        pIAssessorFindings.push(...pIAssessorFindingsBody);
        partiallyImplementedSection.push(...pIAssessorFindings);
      }

      questionArray.push(...[
        qContain.answer?.comment && { text: `Service Provider Comment: ${qContain.answer?.comment}` },
        {
          table: {
            widths: '*',
            body: [
              [
                {
                  border: [false, false, false, false],
                  fillColor: qContain.assessorAnswer.answer === 'Not Implemented' ? appTheme.palette.error.main : qContain.assessorAnswer.answer === 'Partially Implemented' ? appTheme.palette.nonCompliant.main : appTheme.palette.success.main,
                  color: qContain.assessorAnswer.answer === 'Partially Implemented' ? 'black' : 'white',
                  text: qContain.assessorAnswer.answer,
                },
              ],
            ],
          },
          margin: [0, 10, 0, 5],
        },
        ...partiallyImplementedSection, // Insert the partially implemented section
        { text: 'Assessor Finding Comment:', bold: true, margin: [0, 10, 0, 0] },
        qContain.assessorAnswer.assessorFinding,
      ]);

      const showAttachments: boolean = qContain.assessorAnswer.vendorStatus === REMEDIATION_OPTIONS.remediated && qContain.assessorAnswer?.evidenceAttachments?.length > 0;
      const attachmentText = showAttachments ? qContain.assessorAnswer.evidenceAttachments
        ?.map((attachment: any) => {
          const fileName = getFileName(attachment.file.document);
          return `${fileName} (${attachment.isPublic ? 'Public' : 'Private'})`;
        })
        .join(', ') : '';

      if (qContain.assessorAnswer.vendorStatus) {
        questionArray.push(...[
          { text: 'Service Provider Remediation Plan:', bold: true, margin: [0, 10, 0, 0] },
          {
            margin: [10, 0, 0, 0],
            ul: [
              { text: [{ text: 'Remediation Plan: ', bold: true }, `${REMEDIATION_OPTIONS_LABELS[qContain.assessorAnswer.vendorStatus as keyof typeof REMEDIATION_OPTIONS_LABELS]}`], margin: [0, 5, 0, 0] },
              qContain.assessorAnswer.vendorStatus === REMEDIATION_OPTIONS.willRemediateLater
                ? { text: [{ text: 'Future Remediation Date: ', bold: true }, `${qContain.assessorAnswer.remediationDate ? moment(qContain.assessorAnswer.remediationDate).format('MMM D, YYYY') : 'Not available' }`], margin: [0, 5, 0, 0] }
                : '',
              (qContain.assessorAnswer.vendorStatus === REMEDIATION_OPTIONS.remediated || qContain.assessorAnswer.vendorStatus === REMEDIATION_OPTIONS.willNotRemediate)
                ? { text: [{ text: 'Remediation Date: ', bold: true }, `${qContain.assessorAnswer.remediationDate ? moment(qContain.assessorAnswer.remediationDate).format('MMM D, YYYY') : 'Not available' }`], margin: [0, 5, 0, 0] }
                : '',
              (showAttachments)
                ? { text: [{ text: 'Remediation Evidence: ', bold: true }, `${attachmentText}`], margin: [0, 5, 0, 0] }
                : '',
              (qContain.assessorAnswer.vendorStatus === REMEDIATION_OPTIONS.willNotRemediate)
                ? { text: [{ text: 'Reason for Not Remediating: ', bold: true }, `${WILL_NOT_REMEDIATE_REASON_LABELS[qContain?.assessorAnswer?.willNotRemediateReason as keyof typeof WILL_NOT_REMEDIATE_REASON_LABELS] || 'Not available'}`], margin: [0, 5, 0, 0] }
                : '',
              { text: [{ text: 'Remediation Comment: ', bold: true }, `${qContain.assessorAnswer.remediationComment || 'Not available'}`], margin: [0, 5, 0, 0] },
            ].filter(Boolean),
          },
        ]);
      }
      item.push({
        table: {
          headerRows: 0,
          widths: ['*'],
          body: [
            [{
              stack: questionArray,
            }],
          ],
        },
        margin: [0, 20, 0, 0],
      });
    });
    if (index + 1 !== bpDisplay.length) return { stack: item, margin: [0, 10, 0, 20] };
    return { stack: item };
  });

  return bpItems;
};
